import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { useAuth } from '@/contexts/AuthContext'
import { getTimesheets } from '@/core/timesheet'

const DEFAULT_HOURS_PER_WEEK_GOAL = 40
const SECONDS_PER_HOUR = 3600

/**
 * Returns the current user's total tracked seconds this week (Mon–Sun),
 * their weekly hours goal (from user.weeklyHoursQuota or default 40),
 * and loading state. Does not throw when user is not authenticated.
 */
export function useTimesheetHoursThisWeek() {
  const { userInfo } = useAuth()
  const userId = userInfo?.id ?? null
  const hoursGoal =
    userInfo?.weeklyHoursQuota != null && userInfo.weeklyHoursQuota > 0
      ? userInfo.weeklyHoursQuota
      : DEFAULT_HOURS_PER_WEEK_GOAL

  const {
    data: timesheets = [],
    isLoading: isLoadingTimesheets,
  } = useQuery({
    queryKey: ['timesheets', 'hoursThisWeek', userId],
    queryFn: async () => {
      if (!userId) return []
      return getTimesheets({
        userId,
        clients: [],
        projects: [],
        projectAssignments: [],
      })
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })

  const { hoursThisWeekSeconds, hoursThisWeekDecimal } = useMemo(() => {
    if (!timesheets.length) {
      return { hoursThisWeekSeconds: 0, hoursThisWeekDecimal: 0 }
    }
    const now = DateTime.now()
    const startOfWeek = now.startOf('week') // Monday
    const endOfWeek = now.endOf('week') // Sunday

    let totalSeconds = 0
    for (const entry of timesheets) {
      const entryDate = entry.date ? DateTime.fromISO(entry.date) : DateTime.fromISO(entry.startTime)
      if (!entryDate.isValid) continue
      if (entryDate >= startOfWeek && entryDate <= endOfWeek) {
        totalSeconds += entry.duration ?? 0
      }
    }

    return {
      hoursThisWeekSeconds: totalSeconds,
      hoursThisWeekDecimal: totalSeconds / SECONDS_PER_HOUR,
    }
  }, [timesheets])

  return {
    hoursThisWeekSeconds,
    hoursThisWeekDecimal,
    hoursGoal,
    isLoading: isLoadingTimesheets,
    hasTrackedHoursThisWeek: hoursThisWeekSeconds > 0,
  }
}
